import { inject, Injectable, OnDestroy } from '@angular/core';
import { LocalStorageService } from './local-storage.service';
import { DRAFT_PERSISTANT_CONFIG } from './persistence.interface';
import { StoredEntityData, UserDraftIndex } from './storage.interface';

@Injectable()
export class CleanupService implements OnDestroy {
	private readonly localStorageService = inject(LocalStorageService);
	private readonly config = inject(DRAFT_PERSISTANT_CONFIG);
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	private cleanupIntervalId?: any; // using `any` as will be set to `setInterval` ID

	public ngOnDestroy() {
		this.stop();
	}

	/**
	 * Starts the periodic cleanup process.
	 */
	public start() {
		if (this.cleanupIntervalId) {
			this.stop();
		}
		this.cleanupIntervalId = setInterval(() => {
			this.runCleanup();
		}, this.config.cleanupInterval);
	}

	/**
	 * Stops the periodic cleanup process.
	 */
	public stop() {
		if (!this.cleanupIntervalId) return;
		clearInterval(this.cleanupIntervalId);
		this.cleanupIntervalId = undefined;
	}

	/**
	 * Runs all cleanup tasks.
	 */
	public runCleanup() {
		this.cleanupExpiredDrafts();
		this.cleanupStaleSessions();
		this.manageStorageQuota();
	}

	/**
	 * Removes drafts that have exceeded their TTL.
	 */
	private cleanupExpiredDrafts() {
		const now = Date.now();
		// This is a simplified approach. A real implementation would need a way to get all user indexes.
		// For now, we assume we can get all keys and filter them.
		for (let i = 0; i < localStorage.length; i++) {
			const key = localStorage.key(i);
			if (!key || !key.startsWith('lvr_draft_')) continue;
			const draft = this.localStorageService.getDraft(key);
			if (draft && draft.metadata && draft.metadata.expiresAt < now) {
				this.localStorageService.removeItem(key);
				// Also remove from the corresponding UserDraftIndex
				this.removeDraftKeyFromIndex(draft.metadata.userId, key);
			}
		}
	}

	/**
	 * Removes drafts from stale sessions.
	 */
	private cleanupStaleSessions() {
		const now = Date.now();
		const staleThreshold = this.config.staleThreshold;
		// This is a simplified approach. A real implementation would need a way to get all user indexes.
		for (let i = 0; i < localStorage.length; i++) {
			const key = localStorage.key(i);
			if (!key || !key.startsWith('lvr_index_')) continue;
			const index = this.localStorageService.getItem<UserDraftIndex>(key);
			if (!index || now - index.lastActivity <= staleThreshold) continue;
			index.draftKeys.forEach((draftKey) => this.localStorageService.removeItem(draftKey));
			this.localStorageService.removeItem(key);
		}
	}

	/**
	 * Manages storage quota by removing the oldest drafts if the quota is exceeded.
	 */
	private manageStorageQuota() {
		const storageQuota = this.config.storageQuota;
		let totalSize = 0;
		const drafts: { key: string; draft: StoredEntityData }[] = [];

		for (let i = 0; i < localStorage.length; i++) {
			const key = localStorage.key(i);
			if (!key) continue;
			const item = localStorage.getItem(key);
			if (!item) continue;
			totalSize += item.length;
			if (!key.startsWith('lvr_draft_')) continue;
			const draft = this.localStorageService.getDraft(key);
			if (!draft) continue;
			drafts.push({ key, draft });
		}

		if (totalSize <= storageQuota) return;
		drafts.sort((a, b) => a.draft.metadata.lastModified - b.draft.metadata.lastModified);
		const draftsToRemove = Math.ceil(drafts.length * 0.2); // Remove oldest 20%
		for (let i = 0; i < draftsToRemove; i++) {
			const draftToRemove = drafts[i];
			this.localStorageService.removeItem(draftToRemove.key);
			this.removeDraftKeyFromIndex(draftToRemove.draft.metadata.userId, draftToRemove.key);
		}
	}

	private removeDraftKeyFromIndex(userId: string, draftKey: string) {
		const index = this.localStorageService.getUserDraftIndex(userId);
		if (!index) return;
		index.draftKeys = index.draftKeys.filter((k) => k !== draftKey);
		this.localStorageService.setUserDraftIndex(userId, index);
	}
}
